import { useState } from 'react';
import { FiX, FiArrowUpCircle } from 'react-icons/fi';
import { api } from '../../services/api';
import { useI18n } from '../../hooks/useI18n';
import { translateErrorMessage } from '../../utils/errorTranslator';

export function WithdrawModal({ open, onClose, wallet, pushToast, onSuccess }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [toAddress, setToAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [token, setToken] = useState('USDC');
  const slippageBps = 30;
  const slippageFactor = 1 - slippageBps / 10000;

  if (!open || !wallet) return null;

  const isSafe = wallet.wallet_type?.toLowerCase() === 'safe';
  const isPrivy = wallet.wallet_provider === 'privy';
  const availableTokens = isSafe || isPrivy ? ['USDC'] : ['USDC', 'POL'];

  // Calculate total USDC
  const usdcNative = wallet.usdc_native || 0;
  const usdcBridged = wallet.usdc_balance || 0;
  const bridgedConvertible = usdcBridged >= 0.5 ? usdcBridged * slippageFactor : 0;
  const totalUsdc = usdcNative + bridgedConvertible;
  const maxWithdrawUsdc = Math.max(0, Math.floor(totalUsdc * 1_000_000) / 1_000_000);

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 80;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const statusRes = await api.getTransferJobStatus(jobId);
        const data = statusRes.data || {};
        if (data.status === 'success') {
          pushToast?.(t('wallet.withdrawSuccess'), 'success');
          setLoading(false);
          onClose();
          onSuccess?.();
          return;
        }
        if (data.status === 'processing' && data.tx_hash) {
          pushToast?.(t('wallet.withdrawSubmitted'), 'info');
          setLoading(false);
          onClose();
          onSuccess?.();
          return;
        }
        if (data.status === 'failed') {
          pushToast?.(translateErrorMessage(data.error_msg, t) || t('wallet.withdrawFailed'), 'error');
          setLoading(false);
          return;
        }
      } catch (err) {
        // Ignore single query error
      }
      await new Promise((r) => setTimeout(r, 3000));
    }
    pushToast?.(t('wallet.withdrawTimeout'), 'warning');
    setLoading(false);
  };

  const handleWithdraw = async () => {
    if (!toAddress || !amount) {
      pushToast?.(t('wallet.fillAll'), 'warning');
      return;
    }
    if (!toAddress.startsWith('0x') || toAddress.length !== 42) {
      pushToast?.(t('wallet.invalidAddress'), 'warning');
      return;
    }
    if (parseFloat(amount) <= 0) {
      pushToast?.(t('wallet.amountGt0'), 'warning');
      return;
    }

    try {
      setLoading(true);
      const walletAddress = wallet.safe_address || wallet.wallet_address;
      const res = await api.transfer(walletAddress, toAddress, amount, token, true);

      if (res.code === 'SUCCESS') {
        pushToast?.(t('wallet.withdrawSubmitOk'), 'info');
        const jobId = res.data?.job_id;
        if (jobId) {
          pollJobStatus(jobId);
        } else {
          setLoading(false);
        }
      } else {
        pushToast?.(translateErrorMessage(res.msg, t) || t('wallet.withdrawFailed'), 'error');
        setLoading(false);
      }
    } catch (e) {
      pushToast?.(translateErrorMessage(e?.message, t) || t('wallet.requestFailed'), 'error');
      setLoading(false);
    }
  };

  const displayAddress = wallet.safe_address || wallet.wallet_address;

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div>
            <h3 className="text-lg font-bold text-white">{t('wallet.withdraw')}</h3>
            <p className="text-sm text-gray-400">
              {t('wallet.fromWallet')} {displayAddress.slice(0, 6)}...{displayAddress.slice(-4)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* From Wallet Info */}
          <div className="p-3 bg-surface rounded-xl border border-glass-border flex items-center justify-between">
            <span className="text-xs text-gray-500">{t('wallet.fromWallet')}</span>
            <span className="text-xs font-mono text-gray-300">
              {displayAddress.slice(0, 6)}...{displayAddress.slice(-4)}
            </span>
          </div>

          {/* Token Selection */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-400">{t('wallet.token')}</label>
            <div className="flex gap-2">
              {availableTokens.map((tokenSymbol) => (
                <button
                  key={tokenSymbol}
                  onClick={() => setToken(tokenSymbol)}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    token === tokenSymbol
                      ? 'bg-accent text-white shadow-lg shadow-accent/20'
                      : 'bg-surface text-gray-400 hover:bg-surface-highlight hover:text-white'
                  }`}
                >
                  {tokenSymbol}
                </button>
              ))}
            </div>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <label className="text-xs font-medium text-gray-400">{t('wallet.amount')}</label>
              <span className="text-xs text-gray-500">
                {t('wallet.balance')}: {token === 'USDC' ? maxWithdrawUsdc.toFixed(6) : wallet.pol_balance} {token}
              </span>
            </div>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full bg-surface border border-glass-border rounded-xl pl-4 pr-16 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all"
              />
              <button
                onClick={() => setAmount(token === 'USDC' ? maxWithdrawUsdc.toString() : wallet.pol_balance?.toString())}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-accent hover:text-accent-soft font-medium px-2 py-1 rounded hover:bg-accent/10 transition-colors z-10"
              >
                {t('wallet.max')}
              </button>
            </div>
          </div>

          {/* To Address Input */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-400">{t('wallet.toAddress')}</label>
            <input
              type="text"
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              placeholder="0x..."
              className="w-full bg-surface border border-glass-border rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all font-mono text-sm"
            />
            <p className="text-[10px] text-gray-500">
              {isSafe || isPrivy ? t('wallet.safeHint') : t('wallet.eoaHint')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-glass-border flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors text-sm font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleWithdraw}
            disabled={loading}
            className="px-6 py-2 rounded-xl bg-accent hover:bg-accent-soft text-white text-sm font-medium shadow-lg shadow-accent/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiArrowUpCircle />
            )}
            {t('wallet.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
